import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Send, MessageSquare, Paperclip, Mic, Square, FileText, Download,
  Check, CheckCheck, UserSquare, Loader2, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { ConexionAPI, haceCuanto, ROL_META } from "../../lib/conexion";
import { PlanBadge } from "../../components/PlanBadge";
import { fetchAdjuntoBlob } from "../../lib/portafolio";
import AuthedImage from "../../components/AuthedImage";

function OnlineDot({ online, className = "" }) {
  return (
    <span
      title={online ? "Activo" : "Inactivo"}
      data-testid={`presence-${online ? "online" : "offline"}`}
      className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${online ? "bg-emerald-500" : "bg-slate-300"} ${className}`}
    />
  );
}

function Avatar({ nombre, online }) {
  const ini = nombre?.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "U";
  return (
    <div className="relative flex-shrink-0">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-blue to-brand-navy text-white text-sm font-bold flex items-center justify-center">
        {ini}
      </div>
      {online != null && <OnlineDot online={online} />}
    </div>
  );
}

function AudioNota({ url, propio }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let obj = null, cancelled = false;
    fetchAdjuntoBlob(url).then((u) => { if (cancelled) { URL.revokeObjectURL(u); return; } obj = u; setSrc(u); }).catch(() => {});
    return () => { cancelled = true; if (obj) URL.revokeObjectURL(obj); };
  }, [url]);
  return (
    <div className="flex items-center gap-2 min-w-[220px]" data-testid="chat-audio">
      <Mic size={16} className={propio ? "text-red-100" : "text-brand-red"} />
      {src ? (
        <audio controls src={src} className="h-9 max-w-[220px]" />
      ) : (
        <span className="text-xs opacity-70">Cargando audio…</span>
      )}
    </div>
  );
}

function ArchivoAdjunto({ adjunto, propio }) {
  const [descargando, setDescargando] = useState(false);
  async function descargar() {
    setDescargando(true);
    try {
      const objUrl = await fetchAdjuntoBlob(adjunto.url);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = adjunto.nombre || "archivo";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 4000);
    } finally {
      setDescargando(false);
    }
  }
  const kb = adjunto.size ? Math.round(adjunto.size / 1024) : null;
  return (
    <button
      onClick={descargar}
      data-testid="chat-archivo"
      className={`flex items-center gap-2.5 min-w-[200px] text-left ${propio ? "text-white" : "text-slate-800"}`}
    >
      <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${propio ? "bg-white/20" : "bg-slate-100"}`}>
        {descargando ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold truncate max-w-[180px]">{adjunto.nombre}</span>
        <span className={`text-[11px] inline-flex items-center gap-1 ${propio ? "text-red-100" : "text-slate-500"}`}>
          <Download size={11} /> Descargar{kb ? ` · ${kb} KB` : ""}
        </span>
      </span>
    </button>
  );
}

function MessageBubble({ m, propio }) {
  return (
    <div className={`flex ${propio ? "justify-end" : "justify-start"}`}>
      <div
        className={[
          "max-w-[78%] rounded-2xl px-3.5 py-2 text-sm shadow-sm",
          propio ? "bg-brand-red text-white rounded-br-sm" : "bg-white border border-slate-100 text-slate-800 rounded-bl-sm",
        ].join(" ")}
        data-testid={`chat-msg-${m.id}`}
      >
        {m.tipo === "imagen" && m.adjunto ? (
          <AuthedImage url={m.adjunto.url} alt={m.adjunto.nombre} className="rounded-lg max-h-60 w-auto object-cover" testid="chat-imagen" fallback={<span className="text-xs opacity-70">Imagen no disponible</span>} />
        ) : m.tipo === "audio" && m.adjunto ? (
          <AudioNota url={m.adjunto.url} propio={propio} />
        ) : m.tipo === "archivo" && m.adjunto ? (
          <ArchivoAdjunto adjunto={m.adjunto} propio={propio} />
        ) : (
          <p className="whitespace-pre-wrap break-words">{m.texto}</p>
        )}
        <p className={`text-[10px] mt-1 flex items-center gap-1 justify-end ${propio ? "text-red-100" : "text-slate-400"}`}>
          {haceCuanto(m.fecha)}
          {propio && (
            m.leido
              ? <CheckCheck size={13} className="text-sky-200" data-testid="receipt-leido" />
              : <Check size={13} className="text-red-100" data-testid="receipt-entregado" />
          )}
        </p>
      </div>
    </div>
  );
}

export default function ConexionMensajes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [conversaciones, setConversaciones] = useState([]);
  const [activa, setActiva] = useState(params.get("conv") || null);
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState("");
  const [loading, setLoading] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const [grabando, setGrabando] = useState(false);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recStartRef = useRef(0);

  async function cargarConversaciones() {
    const list = await ConexionAPI.listarConversaciones();
    setConversaciones(list);
    setLoading(false);
    if (!activa && list.length > 0) setActiva(list[0].id);
  }

  async function cargarMensajes(convId) {
    if (!convId) return;
    const msgs = await ConexionAPI.obtenerMensajes(convId);
    setMensajes(msgs);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }

  // Heartbeat de presencia + refresco de conversaciones (dots en línea)
  useEffect(() => {
    ConexionAPI.pingPresencia();
    cargarConversaciones();
    const ping = setInterval(() => ConexionAPI.pingPresencia(), 30000);
    const refresh = setInterval(() => cargarConversaciones(), 20000);
    return () => { clearInterval(ping); clearInterval(refresh); };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (activa) {
      cargarMensajes(activa);
      const t = setInterval(() => cargarMensajes(activa), 3000);
      setParams({ conv: activa });
      return () => clearInterval(t);
    }
    // eslint-disable-next-line
  }, [activa]);

  async function enviar(e) {
    e?.preventDefault?.();
    const t = texto.trim();
    if (!t || !activa) return;
    setTexto("");
    const nuevo = await ConexionAPI.enviarMensaje(activa, t);
    setMensajes((prev) => [...prev, nuevo]);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    cargarConversaciones();
  }

  async function subirArchivo(file, duracion) {
    if (!file || !activa) return;
    setSubiendo(true);
    try {
      const nuevo = await ConexionAPI.enviarAdjunto(activa, file, duracion);
      setMensajes((prev) => [...prev, nuevo]);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      cargarConversaciones();
    } catch (err) {
      console.error("[Mensajes] no se pudo enviar el adjunto:", err);
      toast.error("No se pudo enviar el archivo. Intenta nuevamente.");
    } finally {
      setSubiendo(false);
    }
  }

  function onPickFile(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) subirArchivo(f);
  }

  async function toggleGrabar() {
    if (grabando) {
      recorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      recStartRef.current = Date.now();
      rec.ondataavailable = (ev) => { if (ev.data.size > 0) chunksRef.current.push(ev.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const dur = Math.round((Date.now() - recStartRef.current) / 1000);
        const file = new File([blob], `nota-voz-${Date.now()}.webm`, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        setGrabando(false);
        if (blob.size > 0) subirArchivo(file, dur);
      };
      recorderRef.current = rec;
      rec.start();
      setGrabando(true);
    } catch (_) {
      alert("No se pudo acceder al micrófono. Revisa los permisos del navegador.");
    }
  }

  const activaObj = conversaciones.find((c) => c.id === activa);
  const otro = activaObj?.participantes?.[0];

  return (
    <div
      className="bg-white rounded-2xl border border-slate-100 shadow-soft overflow-hidden grid grid-cols-1 md:grid-cols-[320px_1fr]"
      style={{ minHeight: "560px" }}
      data-testid="conexion-mensajes"
    >
      {/* Lista */}
      <aside className={`border-r border-slate-100 md:block ${activa ? "hidden md:block" : "block"}`}>
        <div className="p-4 border-b border-slate-100">
          <h3 className="font-bold text-slate-900">Conversaciones</h3>
          <p className="text-xs text-slate-500">{conversaciones.length} en total</p>
        </div>
        <div className="overflow-y-auto cl-scroll-light" style={{ maxHeight: "520px" }}>
          {loading ? (
            <p className="p-4 text-sm text-slate-500">Cargando…</p>
          ) : conversaciones.length === 0 ? (
            <div className="p-6 text-center">
              <MessageSquare size={22} className="mx-auto text-slate-300" />
              <p className="mt-2 text-sm text-slate-500">Aún no tienes conversaciones. Contacta desde una publicación.</p>
            </div>
          ) : (
            conversaciones.map((c) => {
              const o = c.participantes[0];
              const active = c.id === activa;
              return (
                <button
                  key={c.id}
                  onClick={() => setActiva(c.id)}
                  data-testid={`conexion-conv-${c.id}`}
                  className={["w-full text-left p-4 border-b border-slate-50 flex items-start gap-3 transition-colors", active ? "bg-brand-blue/5" : "hover:bg-slate-50"].join(" ")}
                >
                  <Avatar nombre={o?.nombre} online={o?.en_linea} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-slate-800 truncate">{o?.nombre || "Usuario"}</p>
                      {c.no_leidos > 0 && (
                        <span className="bg-brand-red text-white text-[10px] font-bold px-1.5 rounded-full min-w-[18px] text-center">{c.no_leidos}</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{c.ultimo_mensaje?.texto || "Sin mensajes todavía"}</p>
                    {c.ultimo_mensaje && <p className="text-[10px] text-slate-400 mt-0.5">{haceCuanto(c.ultimo_mensaje.fecha)}</p>}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </aside>

      {/* Chat */}
      <section className={`flex flex-col ${activa ? "block" : "hidden md:flex"}`}>
        {activa ? (
          <>
            <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <Avatar nombre={otro?.nombre} online={otro?.en_linea} />
                <div>
                  <p className="font-semibold text-slate-800 leading-tight inline-flex items-center gap-1.5">{otro?.nombre || "Usuario"}<PlanBadge plan={otro?.plan} /></p>
                  <div className="flex items-center gap-2">
                    {otro?.rol && (
                      <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: ROL_META[otro.rol]?.color }}>
                        {ROL_META[otro.rol]?.label}
                      </span>
                    )}
                    <span className={`text-[11px] font-semibold ${otro?.en_linea ? "text-emerald-600" : "text-slate-400"}`}>
                      · {otro?.en_linea ? "Activo" : "Inactivo"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {otro?.id && (
                  <button
                    onClick={() => navigate(`/conexion/perfil/${otro.id}`)}
                    data-testid="chat-ver-perfil"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-blue hover:text-white hover:bg-brand-blue border border-brand-blue/30 px-3 py-1.5 rounded-lg transition-colors"
                  >
                    <UserSquare size={14} /> Ver Perfil / Vitrina
                  </button>
                )}
                <button onClick={() => setActiva(null)} className="md:hidden text-sm text-brand-red font-semibold">Volver</button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto cl-scroll-light p-4 space-y-2 bg-slate-50" style={{ maxHeight: "440px" }}>
              {mensajes.length === 0 ? (
                <p className="text-center text-slate-400 text-sm py-6">Envía el primer mensaje 👋</p>
              ) : (
                mensajes.map((m) => <MessageBubble key={m.id} m={m} propio={m.remitente_id === user?.id} />)
              )}
              <div ref={bottomRef} />
            </div>

            <form onSubmit={enviar} className="p-3 border-t border-slate-100 flex items-center gap-2" data-testid="conexion-chat-form">
              <input ref={fileRef} type="file" className="hidden" onChange={onPickFile}
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={subiendo}
                data-testid="chat-adjuntar" title="Adjuntar imagen o archivo"
                className="w-11 h-11 rounded-xl text-slate-500 hover:bg-slate-100 disabled:opacity-40 flex items-center justify-center transition-colors">
                {subiendo ? <Loader2 size={18} className="animate-spin" /> : <Paperclip size={18} />}
              </button>
              <button type="button" onClick={toggleGrabar}
                data-testid="chat-nota-voz" title={grabando ? "Detener y enviar" : "Grabar nota de voz"}
                className={`w-11 h-11 rounded-xl flex items-center justify-center transition-colors ${grabando ? "bg-brand-red text-white animate-pulse" : "text-slate-500 hover:bg-slate-100"}`}>
                {grabando ? <Square size={16} /> : <Mic size={18} />}
              </button>
              <input
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder={grabando ? "Grabando nota de voz…" : "Escribe un mensaje…"}
                disabled={grabando}
                data-testid="conexion-chat-input"
                className="flex-1 h-11 px-4 rounded-xl border border-slate-200 bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none text-sm disabled:bg-slate-50"
              />
              <button type="submit" disabled={!texto.trim()} data-testid="conexion-chat-send"
                className="w-11 h-11 rounded-xl bg-brand-red hover:bg-brand-red-hover disabled:opacity-40 text-white flex items-center justify-center transition-colors">
                <Send size={16} />
              </button>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center p-8 text-center">
            <div>
              <MessageSquare size={32} className="mx-auto text-slate-300" />
              <p className="mt-3 text-sm text-slate-500">Selecciona una conversación para chatear.</p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
